import React, { useState } from 'react';

interface WorkflowStep {
  id: string;
  type: string;
  name: string;
  config: any;
  connections: string[];
}

const WorkflowBuilder = () => {
  const [workflow, setWorkflow] = useState<WorkflowStep[]>([]);
  const [selectedStep, setSelectedStep] = useState<string | null>(null);
  const [workflowName, setWorkflowName] = useState('My Workflow');

  const stepTypes = [
    {
      type: 'trigger',
      name: 'Slash Command',
      icon: '⚡',
      description: 'Triggered by a slash command',
      config: { command: '/workflow' }
    },
    {
      type: 'claude',
      name: 'Ask Claude',
      icon: '🤖',
      description: 'Get AI-powered response',
      config: { prompt: '', context: 'general' }
    },
    {
      type: 'message',
      name: 'Send Message',
      icon: '💬',
      description: 'Send a message to a channel',
      config: { channel: '', message: '' }
    },
    {
      type: 'voice',
      name: 'Voice Call',
      icon: '📞',
      description: 'Make a voice call',
      config: { phoneNumber: '', message: '' }
    },
    {
      type: 'email',
      name: 'Send Email',
      icon: '📧',
      description: 'Send an email',
      config: { to: '', subject: '', body: '' }
    },
    {
      type: 'condition',
      name: 'If/Then',
      icon: '🔀',
      description: 'Conditional branching',
      config: { condition: '', truePath: '', falsePath: '' }
    },
    {
      type: 'database',
      name: 'Database Query',
      icon: '🗄️',
      description: 'Query or update database',
      config: { query: '', table: '' }
    },
    {
      type: 'webhook',
      name: 'HTTP Request',
      icon: '🔌',
      description: 'Make an HTTP request',
      config: { url: '', method: 'POST' }
    }
  ];

  const addStep = (stepType: any) => {
    const newStep: WorkflowStep = {
      id: `step_${Date.now()}`,
      type: stepType.type,
      name: stepType.name,
      config: { ...stepType.config },
      connections: []
    };
    setWorkflow([...workflow, newStep]);
  };

  const removeStep = (stepId: string) => {
    setWorkflow(workflow.filter(s => s.id !== stepId));
    setSelectedStep(null);
  };

  const connectSteps = (fromId: string, toId: string) => {
    setWorkflow(workflow.map(step => {
      if (step.id === fromId) {
        return {
          ...step,
          connections: [...step.connections, toId]
        };
      }
      return step;
    }));
  };

  const exportWorkflow = () => {
    const workflowData = {
      name: workflowName,
      steps: workflow,
      created: new Date().toISOString()
    };
    console.log('Exporting workflow:', workflowData);
    alert('Workflow exported! Check console for details.');
  };

  return (
    <div className="space-y-6">
      {/* Workflow Header */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <input
              type="text"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              className="text-2xl font-bold bg-transparent text-green-400 outline-none border-b border-transparent hover:border-gray-600 focus:border-green-400 transition-colors"
            />
            <p className="text-sm text-gray-400 mt-1">
              Build complex automations with visual workflows
            </p>
          </div>
          <button
            onClick={exportWorkflow}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
          >
            Export Workflow
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Step Library */}
        <div className="md:col-span-1 bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-green-400 mb-4">Step Library</h3>
          <div className="space-y-2">
            {stepTypes.map((stepType) => (
              <button
                key={stepType.type}
                onClick={() => addStep(stepType)}
                className="w-full p-3 bg-black rounded-lg border border-gray-600 hover:border-green-400 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{stepType.icon}</span>
                  <div>
                    <div className="font-bold text-yellow-400">{stepType.name}</div>
                    <div className="text-xs text-gray-400">{stepType.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Workflow Canvas */}
        <div className="md:col-span-2 bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-green-400 mb-4">Workflow Canvas</h3>
          {workflow.length > 0 ? (
            <div className="space-y-4">
              {workflow.map((step, index) => (
                <div
                  key={step.id}
                  className={`bg-black p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedStep === step.id
                      ? 'border-green-400'
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                  onClick={() => setSelectedStep(step.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">
                        {stepTypes.find(t => t.type === step.type)?.icon}
                      </span>
                      <div>
                        <div className="font-bold text-yellow-400">{step.name}</div>
                        <div className="text-xs text-gray-400">Step {index + 1}</div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeStep(step.id);
                      }}
                      className="text-red-400 hover:text-red-300"
                    >
                      ✕
                    </button>
                  </div>

                  {selectedStep === step.id && (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <div className="text-sm text-gray-400 mb-2">Configuration:</div>
                      <div className="space-y-2">
                        {Object.entries(step.config).map(([key, value]) => (
                          <div key={key} className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 capitalize">
                              {key}:
                            </span>
                            <input
                              type="text"
                              value={value as string}
                              onChange={(e) => {
                                setWorkflow(workflow.map(s => {
                                  if (s.id === step.id) {
                                    return {
                                      ...s,
                                      config: {
                                        ...s.config,
                                        [key]: e.target.value
                                      }
                                    };
                                  }
                                  return s;
                                }));
                              }}
                              className="flex-1 bg-gray-900 text-green-400 px-2 py-1 rounded text-xs border border-gray-700 focus:border-green-400 focus:outline-none"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {index < workflow.length - 1 && (
                    <div className="text-center mt-2 text-gray-600">↓</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="mb-2">No steps yet</p>
              <p className="text-sm">Add steps from the library to start building</p>
            </div>
          )}
        </div>
      </div>

      {/* Sample Workflows */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Sample Workflows</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-black p-4 rounded border border-gray-600">
            <h4 className="font-bold text-yellow-400 mb-2">Daily Standup Bot</h4>
            <p className="text-sm text-gray-400 mb-2">
              Automatically collect standup updates and summarize with AI
            </p>
            <div className="text-xs text-gray-500">
              Trigger → Collect Updates → Ask Claude → Send Summary → Log to DB
            </div>
          </div>
          <div className="bg-black p-4 rounded border border-gray-600">
            <h4 className="font-bold text-yellow-400 mb-2">Incident Response</h4>
            <p className="text-sm text-gray-400 mb-2">
              Alert team via multiple channels when incidents occur
            </p>
            <div className="text-xs text-gray-500">
              Alert Trigger → Check Severity → Voice Call → Email → Create Ticket
            </div>
          </div>
          <div className="bg-black p-4 rounded border border-gray-600">
            <h4 className="font-bold text-yellow-400 mb-2">Market Alert System</h4>
            <p className="text-sm text-gray-400 mb-2">
              Monitor markets and notify via preferred channel
            </p>
            <div className="text-xs text-gray-500">
              Check Markets → AI Analysis → If Threshold → Multi-Channel Alert
            </div>
          </div>
          <div className="bg-black p-4 rounded border border-gray-600">
            <h4 className="font-bold text-yellow-400 mb-2">Customer Feedback Loop</h4>
            <p className="text-sm text-gray-400 mb-2">
              Process feedback, analyze with AI, and route to teams
            </p>
            <div className="text-xs text-gray-500">
              Form Submit → AI Sentiment → Route by Type → Notify Team → Track
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowBuilder;